import React, { useState, useEffect } from 'react';
import Tesseract from 'tesseract.js';
import {
    FaMagic,
    FaFileUpload,
    FaPaste,
    FaSpinner,
    FaCheck,
    FaTeeth,
    FaUser,
    FaCalendarAlt,
    FaHashtag,
    FaPalette,
    FaStickyNote
} from 'react-icons/fa';

export const parseOrderText = (text) => {
    const result = {
        orderId: '',
        practitionerName: '',
        patientName: '',
        deliveryDate: '',
        teeth: [],
        shade: '',
        workType: '',
        material: '',
        notes: '',
        formRows: [],
        memoLines: [],
        rawText: text || ''
    };

    if (!text) return result;

    // 1. Patient Name
    const patientMatch = text.match(/(?:Patient\s*Name|Patient|Nom\s*Patient)\s*[:=]\s*([^\n\r]+)/i);
    if (patientMatch) {
        result.patientName = patientMatch[1].trim();
    }

    // 2. Order ID / Reference
    const orderIdMatch = text.match(/(?:Order\s*ID|Réf|Reference|Patient\s*Ref|Medit\s*ID|Dossier)\s*[:=]\s*(\w+)/i)
        || text.match(/\b(2\d{7,8}|\d{6,10})\b/);
    if (orderIdMatch) {
        result.orderId = orderIdMatch[1];
    }

    // 3. Practitioner Name
    const partnerMatch = text.match(/(?:Partner|Praticien|Cabinet)\s*[:=]?\s*(?:Cabinet Dentaire (?:du )?)?(Dr\.?\s*[A-Za-zÀ-ÿ\s-]+)/i)
        || text.match(/(Dr\.?\s+[A-Za-zÀ-ÿ\s-]{3,30})/i)
        || text.match(/Partner\s*[:=]\s*([^\n\r]+)/i);
    if (partnerMatch) {
        let name = partnerMatch[1].trim();
        name = name.replace(/\([^)]*\)/g, '').trim();
        result.practitionerName = name;
    }

    // 4. Delivery Date
    const deliveryMatch = text.match(/(?:Delivery Date|Livraison|Pour le)\s*[:=]?\s*(\d{1,2}[\/\.-]\d{1,2}[\/\.-]\d{2,4})/i)
        || text.match(/(\d{2}\/\d{2}\/\d{4})/);
    if (deliveryMatch) {
        const parts = deliveryMatch[1].split(/[\/\.-]/);
        if (parts.length === 3) {
            let day = parts[0].padStart(2, '0');
            let month = parts[1].padStart(2, '0');
            let year = parts[2].length === 2 ? `20${parts[2]}` : parts[2];
            result.deliveryDate = `${year}-${month}-${day}`;
        }
    }

    // 5. Shade (VITA Classical A1-D4, 3D-Master 0M1-5M3, Bleach BL1-BL4)
    const shadeMatch = text.match(/(?:Shade|Teinte|Teint)\s*[:=]?\s*([A-D][1-4]|BL[1-4]|[0-5]M[1-3]|OM[1-3])/i)
        || text.match(/\b([A-D][1-4]|BL[1-4]|[0-5]M[1-3])\b/i);
    if (shadeMatch) {
        result.shade = shadeMatch[1].toUpperCase();
    }

    // 6. Form Information & Memo Parsing
    const lines = text.split('\n');
    let inForm = false;
    let inMemo = false;
    const formRows = [];
    const memoLines = [];

    for (let line of lines) {
        if (/Form Information/i.test(line)) {
            inForm = true;
            inMemo = false;
            continue;
        }
        if (/Memo/i.test(line)) {
            inForm = false;
            inMemo = true;
            continue;
        }
        if (inForm) {
            const rowMatch = line.match(/^([1-4][1-8])\s+(.+)$/i);
            if (rowMatch && !/No\.\s+Type/i.test(line)) {
                formRows.push({
                    tooth: rowMatch[1],
                    detailsStr: rowMatch[2] || ''
                });
            }
        }
        if (inMemo) {
            if (line.trim() && !/https:\/\/|Copyright/i.test(line)) {
                memoLines.push(line.trim());
            }
        }
    }

    result.formRows = formRows;
    result.memoLines = memoLines;

    // Teeth list
    const teethFound = new Set();
    formRows.forEach(r => teethFound.add(r.tooth));

    if (teethFound.size === 0) {
        const formSection = text.split(/Form Information/i)[1] || text;
        const cleanForm = formSection
            .replace(/\d{1,2}[\/\.-]\d{1,2}[\/\.-]\d{2,4}/g, '')
            .replace(/\d{1,2}:\d{2}(?::\d{2})?/g, '')
            .replace(/Order ID\s*:\s*\d+/gi, '')
            .replace(/Scanned Date\s*:\s*[^\n\r]+/gi, '')
            .replace(/Order Date\s*:\s*[^\n\r]+/gi, '');

        const tRegex = /\b([1-4][1-8])\b/g;
        let match;
        while ((match = tRegex.exec(cleanForm)) !== null) {
            teethFound.add(match[1]);
        }
    }

    result.teeth = Array.from(teethFound).sort((a, b) => Number(a) - Number(b));

    // Combine memo lines for full technical notes
    if (memoLines.length > 0) {
        result.notes = memoLines.join('\n');
    } else {
        const memoMatch = text.match(/(?:Memo|Notes|Consignes|Instructions)\s*[:=]?\s*([\s\S]+)/i);
        result.notes = memoMatch ? memoMatch[1].trim() : '';
    }

    return result;
};

const OrderScreenshotParser = ({ onApplyData }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [statusText, setStatusText] = useState('');
    const [imagePreview, setImagePreview] = useState(null);
    const [parsedData, setParsedData] = useState(null);
    const [manualText, setManualText] = useState('');
    const [activeInputTab, setActiveInputTab] = useState('image');

    const processImageFile = async (file) => {
        if (!file || !file.type.startsWith('image/')) {
            alert('Veuillez sélectionner ou coller un fichier image valide (.png, .jpg, .webp).');
            return;
        }

        const previewUrl = URL.createObjectURL(file);
        setImagePreview(previewUrl);
        setLoading(true);
        setStatusText("Initialisation du lecteur de capture...");

        try {
            const worker = await Tesseract.createWorker('fra+eng');
            setStatusText("Analyse et lecture du texte sur l'image...");
            const ret = await worker.recognize(file);
            const extractedText = ret.data.text;
            await worker.terminate();

            const parsed = parseOrderText(extractedText);
            setParsedData(parsed);
            setLoading(false);
            setStatusText("Analyse terminée !");
        } catch (error) {
            console.error('OCR Error:', error);
            setLoading(false);
            alert("Erreur lors de la lecture de l'image. Vous pouvez aussi coller le texte directement.");
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            processImageFile(e.target.files[0]);
        }
    };

    const handlePasteEvent = (e) => {
        if (!isOpen) return;
        const items = e.clipboardData?.items;
        if (!items) return;

        for (let i = 0; i < items.length; i++) {
            if (items[i].type.indexOf('image') !== -1) {
                const blob = items[i].getAsFile();
                processImageFile(blob);
                e.preventDefault();
                return;
            }
        }
    };

    useEffect(() => {
        if (isOpen) {
            window.addEventListener('paste', handlePasteEvent);
            return () => window.removeEventListener('paste', handlePasteEvent);
        }
    }, [isOpen]);

    const handleParseManualText = () => {
        if (!manualText.trim()) return;
        setLoading(true);
        const parsed = parseOrderText(manualText);
        setParsedData(parsed);
        setLoading(false);
    };

    const handleApply = () => {
        if (parsedData && onApplyData) {
            onApplyData(parsedData);
            setIsOpen(false);
            setParsedData(null);
            setImagePreview(null);
        }
    };

    return (
        <>
            <button
                type="button"
                className="btn text-white fw-bold shadow-sm d-inline-flex align-items-center gap-2 px-3 py-2"
                onClick={() => setIsOpen(true)}
                style={{
                    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '14px',
                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
                }}
            >
                <FaMagic /> Pré-remplir
            </button>

            {isOpen && (
                <div
                    className="modal show d-block"
                    style={{ backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(5px)', zIndex: 1055 }}
                >
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div
                            className="modal-content border-0 shadow-lg"
                            style={{
                                borderRadius: '20px',
                                overflow: 'hidden',
                                backgroundColor: '#ffffff',
                                color: '#0f172a'
                            }}
                        >
                            {/* Modal Header */}
                            <div
                                className="modal-header p-4"
                                style={{
                                    background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)',
                                    color: '#ffffff',
                                    borderBottom: 'none'
                                }}
                            >
                                <div className="d-flex align-items-center gap-3">
                                    <div className="p-3 bg-white bg-opacity-20 rounded-3 text-white">
                                        <FaMagic size={24} />
                                    </div>
                                    <div>
                                        <h5 className="modal-title fw-bold m-0" style={{ color: '#ffffff', fontSize: '18px' }}>
                                            Importateur Intelligent Medit / 3Shape
                                        </h5>
                                        <small style={{ color: '#e2e8f0', fontSize: '13px' }}>
                                            Glissez-déposez ou collez (Ctrl+V) une capture pour pré-remplir la fiche
                                        </small>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    onClick={() => setIsOpen(false)}
                                />
                            </div>

                            {/* Modal Body */}
                            <div className="modal-body p-4" style={{ backgroundColor: '#ffffff', color: '#0f172a' }}>
                                {/* Tab selector */}
                                <div className="d-flex justify-content-center gap-2 mb-4">
                                    <button
                                        type="button"
                                        className="btn fw-semibold px-4 py-2"
                                        onClick={() => setActiveInputTab('image')}
                                        style={{
                                            backgroundColor: activeInputTab === 'image' ? '#2563eb' : '#f1f5f9',
                                            color: activeInputTab === 'image' ? '#ffffff' : '#334155',
                                            borderRadius: '10px',
                                            border: 'none'
                                        }}
                                    >
                                        <FaFileUpload className="me-2" /> Image / Capture d'écran
                                    </button>
                                    <button
                                        type="button"
                                        className="btn fw-semibold px-4 py-2"
                                        onClick={() => setActiveInputTab('text')}
                                        style={{
                                            backgroundColor: activeInputTab === 'text' ? '#2563eb' : '#f1f5f9',
                                            color: activeInputTab === 'text' ? '#ffffff' : '#334155',
                                            borderRadius: '10px',
                                            border: 'none'
                                        }}
                                    >
                                        <FaPaste className="me-2" /> Copier / Coller Texte
                                    </button>
                                </div>

                                {activeInputTab === 'image' ? (
                                    <div
                                        className="rounded-4 p-4 text-center mb-4 position-relative"
                                        style={{
                                            backgroundColor: '#f8fafc',
                                            border: '2px dashed #2563eb',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                                                processImageFile(e.dataTransfer.files[0]);
                                            }
                                        }}
                                    >
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            className="position-absolute top-0 start-0 w-100 h-100 opacity-0 cursor-pointer"
                                        />
                                        <div className="my-3">
                                            <div
                                                className="d-inline-flex p-3 rounded-circle mb-3"
                                                style={{ backgroundColor: '#eff6ff', color: '#2563eb' }}
                                            >
                                                <FaFileUpload size={32} />
                                            </div>
                                            <h6 className="fw-bold mb-1" style={{ color: '#0f172a', fontSize: '16px' }}>
                                                Glissez une image ici ou cliquez pour choisir
                                            </h6>
                                            <p className="mb-2" style={{ color: '#475569', fontSize: '13px' }}>
                                                Vous pouvez aussi appuyer directement sur <strong>Ctrl + V</strong> pour coller une capture
                                            </p>
                                            <span
                                                className="badge fw-semibold"
                                                style={{
                                                    backgroundColor: '#eff6ff',
                                                    color: '#1d4ed8',
                                                    border: '1px solid #bfdbfe',
                                                    padding: '6px 12px',
                                                    borderRadius: '8px',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                Formats acceptés : PNG, JPG, WEBP
                                            </span>
                                        </div>

                                        {imagePreview && (
                                            <div className="mt-3 p-2 bg-white rounded border d-inline-block shadow-sm">
                                                <img src={imagePreview} alt="Aperçu" style={{ maxHeight: '140px', borderRadius: '8px' }} />
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="mb-4">
                                        <label className="form-label fw-bold small" style={{ color: '#475569' }}>
                                            Collez le texte brut de la commande Medit :
                                        </label>
                                        <textarea
                                            className="form-control p-3"
                                            rows="6"
                                            style={{
                                                backgroundColor: '#ffffff',
                                                color: '#0f172a',
                                                borderColor: '#cbd5e1',
                                                borderRadius: '10px'
                                            }}
                                            placeholder="Exemple : Order ID : 27056030&#10;Partner : Cabinet du Dr GHIO Jean-François&#10;Delivery Date : 24/08/2026..."
                                            value={manualText}
                                            onChange={(e) => setManualText(e.target.value)}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm mt-2 fw-semibold px-3 py-1.5"
                                            onClick={handleParseManualText}
                                        >
                                            Analyse du texte
                                        </button>
                                    </div>
                                )}

                                {/* Loading state */}
                                {loading && (
                                    <div className="text-center py-4 bg-light rounded-3 border mb-4">
                                        <FaSpinner className="spinner-border text-primary mb-2" style={{ width: '2rem', height: '2rem' }} />
                                        <p className="fw-bold mb-0" style={{ color: '#0f172a' }}>{statusText}</p>
                                    </div>
                                )}

                                {/* Parsed Results Card */}
                                {parsedData && !loading && (
                                    <div
                                        className="card border-0 shadow-sm rounded-3 overflow-hidden"
                                        style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }}
                                    >
                                        <div className="card-header border-0 py-3" style={{ backgroundColor: '#f0fdf4' }}>
                                            <h6 className="m-0 fw-bold d-flex align-items-center gap-2" style={{ color: '#15803d' }}>
                                                <FaCheck /> Informations détectées avec succès :
                                            </h6>
                                        </div>
                                        <div className="card-body p-3">
                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    <div className="p-2.5 rounded" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                        <small className="d-block mb-1" style={{ color: '#475569', fontWeight: '600' }}>
                                                            <FaUser className="me-1" /> Praticien / Cabinet
                                                        </small>
                                                        <strong style={{ color: '#0f172a', fontSize: '15px' }}>
                                                            {parsedData.practitionerName || 'Non détecté'}
                                                        </strong>
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <div className="p-2.5 rounded" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                        <small className="d-block mb-1" style={{ color: '#475569', fontWeight: '600' }}>
                                                            <FaUser className="me-1" /> Nom du Patient
                                                        </small>
                                                        <strong style={{ color: '#0f172a', fontSize: '15px' }}>
                                                            {parsedData.patientName || 'Non détecté'}
                                                        </strong>
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <div className="p-2.5 rounded" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                        <small className="d-block mb-1" style={{ color: '#475569', fontWeight: '600' }}>
                                                            <FaHashtag className="me-1" /> Référence Commande
                                                        </small>
                                                        <strong style={{ color: '#2563eb', fontFamily: 'monospace', fontSize: '15px' }}>
                                                            {parsedData.orderId || 'Non détecté'}
                                                        </strong>
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <div className="p-2.5 rounded" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                        <small className="d-block mb-1" style={{ color: '#475569', fontWeight: '600' }}>
                                                            <FaCalendarAlt className="me-1" /> Date de Livraison
                                                        </small>
                                                        <strong style={{ color: '#0f172a', fontSize: '15px' }}>
                                                            {parsedData.deliveryDate ? new Date(parsedData.deliveryDate).toLocaleDateString('fr-FR') : 'Non spécifiée'}
                                                        </strong>
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <div className="p-2.5 rounded" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                        <small className="d-block mb-1" style={{ color: '#475569', fontWeight: '600' }}>
                                                            <FaPalette className="me-1" /> Teinte
                                                        </small>
                                                        <span className="badge bg-primary px-2.5 py-1" style={{ fontSize: '13px' }}>
                                                            {parsedData.shade || 'Non spécifiée'}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="col-12">
                                                    <div className="p-2.5 rounded" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                        <small className="d-block mb-1" style={{ color: '#475569', fontWeight: '600' }}>
                                                            <FaTeeth className="me-1" /> Dents sélectionnées ({parsedData.teeth.length})
                                                        </small>
                                                        {parsedData.teeth.length > 0 ? (
                                                            <div className="d-flex flex-wrap gap-1 mt-1">
                                                                {parsedData.teeth.map(t => (
                                                                    <span key={t} className="badge bg-dark px-2 py-1 font-monospace">{t}</span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span style={{ color: '#64748b', fontSize: '13px' }}>Aucune dent détectée</span>
                                                        )}
                                                    </div>
                                                </div>

                                                {parsedData.notes && (
                                                    <div className="col-12">
                                                        <div className="p-2.5 rounded" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                            <small className="d-block mb-1" style={{ color: '#475569', fontWeight: '600' }}>
                                                                <FaStickyNote className="me-1" /> Notes & Consignes du scanner
                                                            </small>
                                                            <small style={{ color: '#0f172a', fontWeight: '500', display: 'block', whiteSpace: 'pre-wrap' }}>
                                                                {parsedData.notes}
                                                            </small>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Modal Footer */}
                            <div
                                className="modal-footer p-3"
                                style={{ backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}
                            >
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary fw-semibold px-4"
                                    onClick={() => setIsOpen(false)}
                                    style={{ borderRadius: '10px' }}
                                >
                                    Fermer
                                </button>
                                {parsedData && (
                                    <button
                                        type="button"
                                        className="btn btn-success fw-bold px-4 py-2 d-flex align-items-center gap-2"
                                        onClick={handleApply}
                                        style={{ borderRadius: '10px' }}
                                    >
                                        <FaMagic /> Appliquer à la Fiche de Travail
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default OrderScreenshotParser;
